import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Text from '../../components/Text/Text';
import './AdminScreen.css';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { useUI } from '../../hooks';
import { apiClient } from '../../api/client';

/**
 * AdminScreen component
 *
 * This component renders the admin dashboard with navigation links
 * and a content area for admin functionality.
 */
const AdminScreen: React.FC = () => {
  const location = useLocation();
  const { showToast } = useUI();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Determine if we're on the main admin page or a sub-page
  const isMainAdminPage = location.pathname === '/admin';
  
  const handleProcessReleasedPayments = async () => {
    setShowConfirmDialog(true);
  };
  
  const confirmProcessPayments = async () => {
    setShowConfirmDialog(false);
    
    try {
      setIsProcessing(true);
      const result = await apiClient.transactions.processReleasedPayments();
      showToast('success', `${result.message}`);
    } catch (error) {
      console.error('Error processing released payments:', error);
      showToast('error', 'Failed to process released payments');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const cancelProcessPayments = () => {
    setShowConfirmDialog(false);
  };
  
  return (
    <div className="admin-screen">
      <div className="admin-header">
        <Text className="text-h1">Admin</Text>
      </div>
      
      <div className="admin-navigation">
        <Link
          to="/admin"
          className={`admin-nav-link ${isMainAdminPage ? 'active' : ''}`}
        >
          Dashboard
        </Link>
        <Link
          to="/admin/transactions"
          className={`admin-nav-link ${location.pathname.includes('/admin/transactions') ? 'active' : ''}`}
        >
          Transactions
        </Link>
        <Link
          to="/admin/notifications"
          className={`admin-nav-link ${location.pathname.includes('/admin/notifications') ? 'active' : ''}`}
        >
          Notification Testing
        </Link>
      </div>
      
      <div className="admin-content">
        {isMainAdminPage ? (
          <div className="admin-dashboard">
            <Text className="text-body">Welcome to the admin dashboard.</Text>
            
            {/* Add Process Released Payments button */}
            <div className="admin-actions">
              <button
                className="admin-action-button primary-button"
                onClick={handleProcessReleasedPayments}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Process Released Payments'}
              </button>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </div>
      
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmationDialog
          title="Process Released Payments"
          message="Are you sure you want to process all released payments? This will create Stripe connected accounts for sellers who don't have one and transfer funds to their accounts."
          confirmText="Process Payments"
          cancelText="Cancel"
          onConfirm={confirmProcessPayments}
          onCancel={cancelProcessPayments}
        />
      )}
    </div>
  );
};

export default AdminScreen;